import React, { useEffect, useMemo, useState } from "react";
import { Nav } from "react-bootstrap";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useResolvedRolePermissions } from "@/contexts/SuperAdminIdentityContext";
import { canViewPage } from "@/features/employees/employeeRolePermissions";
import {
  isSidebarGroupActive,
  isSidebarPageActive,
  SUPER_ADMIN_SIDEBAR_DASHBOARD,
  SUPER_ADMIN_SIDEBAR_GROUPS,
  type SidebarNavGroup,
  type SidebarNavPage,
} from "./sidebarNavConfig";
import SidebarReleaseFlipCard from "./SidebarReleaseFlipCard";
import "./Sidebar.scss";

interface SidebarProps {
  isCollapsed: boolean;
}

function buildInitialOpenState(
  pathname: string,
  groups: SidebarNavGroup[],
): Record<string, boolean> {
  return Object.fromEntries(
    groups.map((group) => [group.id, isSidebarGroupActive(pathname, group)]),
  );
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed }) => {
  const { user } = useAuth0();
  const location = useLocation();
  const permissions = useResolvedRolePermissions();

  const visibleGroups = useMemo(() => {
    return SUPER_ADMIN_SIDEBAR_GROUPS.map((group) => ({
      ...group,
      pages: group.pages.filter((navPage) => canViewPage(permissions, navPage.id)),
    })).filter((group) => group.pages.length > 0);
  }, [permissions]);

  const showDashboard = canViewPage(permissions, SUPER_ADMIN_SIDEBAR_DASHBOARD.id);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    buildInitialOpenState(location.pathname, visibleGroups),
  );

  useEffect(() => {
    setOpenGroups(buildInitialOpenState(location.pathname, visibleGroups));
  }, [location.pathname, visibleGroups]);

  useEffect(() => {
    if (location.pathname === SUPER_ADMIN_SIDEBAR_DASHBOARD.path) {
      setOpenGroups(
        Object.fromEntries(visibleGroups.map((group) => [group.id, false])),
      );
    }
  }, [location.pathname, visibleGroups]);

  const handleDropdownClick = (groupId: string) => {
    setOpenGroups((prev) => {
      const nextOpen = !prev[groupId];
      return Object.fromEntries(
        visibleGroups.map((group) => [
          group.id,
          group.id === groupId ? nextOpen : false,
        ]),
      );
    });
  };

  const renderPageLink = (navPage: SidebarNavPage) => (
    <Nav.Link
      key={navPage.id}
      className={`custom-nav-link${isSidebarPageActive(location.pathname, navPage) ? " active" : ""}`}
      as={NavLink}
      to={navPage.path}
    >
      <span>
        <i className={`bi ${navPage.icon}`} />
      </span>
      <span>{navPage.label}</span>
    </Nav.Link>
  );

  const renderGroup = (group: SidebarNavGroup) => {
    const isOpen = openGroups[group.id] === true;
    return (
      <React.Fragment key={group.id}>
        <div
          className={`sidebar-dropdown${isOpen ? " active" : ""}`}
          onClick={() => handleDropdownClick(group.id)}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            padding: "10px 15px",
          }}
        >
          <span>
            <i className={`bi ${group.icon}`} />
          </span>
          <span style={{ fontWeight: "bold" }}>{group.label}</span>
          <span
            style={{
              marginLeft: "auto",
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "none",
            }}
          >
            ▲
          </span>
        </div>
        {isOpen && (
          <div className="sidebar-submenu">{group.pages.map(renderPageLink)}</div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={`sidebar${isCollapsed ? " collapsed" : ""}`}>
      <div className="sidebar-nav-wrapper">
        <Nav className="flex-column">
          {user && (
            <>
              {showDashboard && (
                <Nav.Link
                  className={`custom-nav-link${isSidebarPageActive(location.pathname, SUPER_ADMIN_SIDEBAR_DASHBOARD) ? " active" : ""}`}
                  as={NavLink}
                  to={SUPER_ADMIN_SIDEBAR_DASHBOARD.path}
                >
                  <span>
                    <i className={`bi ${SUPER_ADMIN_SIDEBAR_DASHBOARD.icon}`} />
                  </span>
                  <span>{SUPER_ADMIN_SIDEBAR_DASHBOARD.label}</span>
                </Nav.Link>
              )}
              {visibleGroups.map(renderGroup)}
            </>
          )}
        </Nav>
      </div>

      <div className="sidebar-version">
        <SidebarReleaseFlipCard
          context={{ kind: "super-admin" }}
          collapsed={isCollapsed}
        />
      </div>
    </div>
  );
};

export default Sidebar;
